/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BlueprintIntegration, TBlueprintIntegrationSchema } from "@adapptio/lib-core";
import {
	DESIGN_CONTEXT_READ_MODE,
	DesignContext,
	ISchemaConstMapModel,
	serializeIDTToYAML,
	TBlueprintIDTNode,
	TGenericBlueprintSchema,
	TGenericModelNode,
	TGetBlueprintSchemaModel
} from "@adapptio/lib-blueprint";
import {
	createEventEmitter,
	emitEvent,
	onEvent,
	removeAllEventListeners,
	UndoRedoManager
} from "@adapptio/lib-shared";
import { IErrorListGroup } from "../../../ui/components/ErrorList/ErrorListTypes";
import { IntegrationService } from "../../../integration/IntegrationService";
import { Debounce } from "../../../decorators";

export const DEFAULT_ENV_NAME = "default";

export class IntegrationModel {
	/* Contexts */
	private dCtx: DesignContext;

	/** Emitted when parse errors has changed */
	public onParseErrors = createEventEmitter<void>();

	/** Emitted when blueprint model has changed */
	public onModelChange = createEventEmitter<void>();

	/** Blueprint model */
	private model: TGetBlueprintSchemaModel<TBlueprintIntegrationSchema> = null;

	/** Undo/redo manager */
	public readonly undoRedoManager = new UndoRedoManager({ historyLimit: 10 });

	/** Last snapshot to compare with current state */
	private lastSnapshot: TBlueprintIDTNode = null;

	/** Flag to prevent snapshot creation during undo/redo */
	private isApplyingSnapshot = false;

	/**
	 * Document constructor
	 */
	public constructor(private integrationSvc: IntegrationService) {
		this.dCtx = new DesignContext({
			resolvers: this.integrationSvc.getBaseResolvers(),
			readMode: DESIGN_CONTEXT_READ_MODE.FULL,
			enableCompletition: true
		});

		onEvent(this.dCtx.modelChangeEvent, (ev) => {
			console.log("Model has changed", ev);
			this.handleModelChange();
		});
	}

	private async initModel(): Promise<void> {
		this.handleModelChange();
	}

	private getOptsMap(): ISchemaConstMapModel<TGenericBlueprintSchema> {
		return this.model.props.spec.opts as ISchemaConstMapModel<TGenericBlueprintSchema>;
	}

	/**
	 * Creates an empty view blueprint
	 */
	public async createDefault(integrationType: string): Promise<void> {
		this.model = BlueprintIntegration.createDefault(this.dCtx, null);

		this.model.props.spec.schema.setName(this.model.props.spec, integrationType, true);

		const optsMap = this.getOptsMap();

		if (!optsMap.items.find((item) => item.key.value === "default")) {
			optsMap.schema.addElement(optsMap, "default", undefined, true).value;
		}

		await this.dCtx.waitForPendingOperations();
		await this.initModel();
	}

	/**
	 * Loads document
	 *
	 * @param blueprintContent Blueprint YAML contents
	 */
	public async loadDocument(blueprintContent: string): Promise<void> {
		this.model = this.dCtx.loadYAMLDocument(
			BlueprintIntegration,
			blueprintContent,
			"local://integration"
		);
		await this.dCtx.waitForPendingOperations();
		await this.initModel();
	}

	/**
	 * Returns document as YAML
	 */
	public exportDocumentAsYAML(): string {
		if (!this.model) {
			throw new Error("Model is not initialized.");
		}

		const idt = this.model.schema.serialize(this.model, [ "$" ]);

		return serializeIDTToYAML(idt);
	}

	/**
	 * Returns design context
	 */
	public getDesignContext(): DesignContext {
		return this.dCtx;
	}

	/**
	 * Returns blueprint model
	 */
	public getModel(): TGetBlueprintSchemaModel<TBlueprintIntegrationSchema> {
		return this.model;
	}

	/**
	 * Returns all errors in a format for error console
	 */
	public getErrors(): IErrorListGroup {
		return {
			documentUri: null,
			parseErrors: this.dCtx.getParseDocumentErrors("local://integration"),
			runtimeErrors: [],
			compileErrors: []
		};
	}

	public getOptsForEnvironment(
		envName: string,
		{ preventCreate }: { preventCreate?: boolean } = {}
	): TGenericModelNode {
		const optsMap = this.getOptsMap();

		for (let i = 0; i < optsMap?.items.length; i++) {
			const item = optsMap.items[i];
			if (item.key.value === envName) {
				return item.value;
			}
		}

		if (!preventCreate) {
			return optsMap.schema.addElement(optsMap, envName, undefined, true).value;
		}
	}

	public removeEnvOpts(envName: string): void {
		const optsMap = this.getOptsMap();

		let index = -1;

		for (let i = 0; i < optsMap?.items.length; i++) {
			const item = optsMap.items[i];
			if (item.key.value === envName) {
				index = i;

				break;
			}
		}

		optsMap.schema.removeElement(optsMap, index, true);
	}

	/**
	 * Serializes the model to IDT for snapshot
	 */
	private serializeModelToIdt(): TBlueprintIDTNode {
		if (!this.model) {
			return null;
		}
		return this.model.schema.serialize(this.model, ["$"]);
	}

	/**
	 * Applies snapshot to model
	 */
	private applySnapshotToModel(snapshot: TBlueprintIDTNode): void {
		if (!snapshot) {
			return;
		}

		this.isApplyingSnapshot = true;
		this.model = BlueprintIntegration.parse(this.dCtx, snapshot);
		this.lastSnapshot = snapshot;
		this.isApplyingSnapshot = false;
		emitEvent(this.onModelChange);
	}

	/**
	 * Creates a snapshot for undo/redo
	 * @param force - If true, creates snapshot even if the state hasn't changed
	 */
	@Debounce(500)
	public createSnapshot(): void {
		if (!this.model || this.isApplyingSnapshot) {
			return;
		}

		const prevSnapshot = this.lastSnapshot;
		const currentSnapshot = this.serializeModelToIdt();
		this.lastSnapshot = currentSnapshot;

		if (prevSnapshot) {
			this.undoRedoManager.add({
				undo: () => this.applySnapshotToModel(prevSnapshot),
				redo: () => this.applySnapshotToModel(currentSnapshot),
				disposeUndo: () => {
					/** */
				},
				disposeRedo: () => {
					/** */
				}
			});

			emitEvent(this.onModelChange);
		}
	}

	private handleModelChange = (): void => {
		this.createSnapshot();
		emitEvent(this.onModelChange);
	};

	public dispose(): void {
		if (this.model) {
			this.model.schema.destroy(this.model);
		}

		this.dCtx.destroy();

		removeAllEventListeners(this.onModelChange);
		this.undoRedoManager.clear();
	}
}
